import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Icon from '../../../../components/common/Icon';
import { ReactComponent as ArrowUpIcon } from '../../../../icons/arrow-up.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../icons/arrow-down.svg';
import useAutoScrollControls from '../../../../hooks/useAutoScrollControls';
import { getSetting, settingKeys } from '../../../../utils/settings';

import './SuperScroll.scss';

const SuperScroll = ({ song, continuousView }) => {
  const { t } = useTranslation();
  const controls = useAutoScrollControls(song);
  // Start hidden: the next-song button is on screen until the observer proves
  // otherwise. This avoids a flash of the controls on first paint, since the
  // IntersectionObserver callback only fires asynchronously after paint.
  const [hidden, setHidden] = useState(true);
  const [decreaseAnimKey, setDecreaseAnimKey] = useState(0);
  const [increaseAnimKey, setIncreaseAnimKey] = useState(0);

  useEffect(() => {
    const target = document.querySelector('.ViewNextSongBtn');
    if (!target) return;

    const observer = new IntersectionObserver(([entry]) => setHidden(entry.isIntersecting), { threshold: 0 });

    observer.observe(target);
    return () => observer.disconnect();
  }, [song?._id, continuousView]);

  if (!getSetting(settingKeys.superScroll)) return null;
  // Up is faster, down is slower — same direction as the arrows everywhere else in the app.
  // In smart mode these step the pace multiplier rather than the speed levels — the schedule owns
  // the velocity, the player owns how hurried it is overall. useAutoScrollControls hides which.
  const handleDecreaseSpeed = () => {
    controls.slowDown();
    setDecreaseAnimKey((k) => k + 1);
  };

  const handleIncreaseSpeed = () => {
    controls.speedUp();
    setIncreaseAnimKey((k) => k + 1);
  };

  return (
    <div className={`SuperScroll ${hidden ? 'hidden' : ''}`}>
      <button
        className={`overlay-btn ${!controls.canSpeedUp ? 'disabled' : ''}`}
        onClick={controls.canSpeedUp ? handleIncreaseSpeed : undefined}
        title={controls.smart ? t('view.faster') : t('view.scrollFaster')}
      >
        <Icon icon={<ArrowUpIcon />} />
        {increaseAnimKey > 0 && <span key={increaseAnimKey} className="btn-ripple btn-ripple--fast" />}
      </button>
      <button
        className={`overlay-btn overlay-speed-label ${controls.smart ? 'smart' : ''} ${
          controls.isScrolling ? 'scrolling' : ''
        }`}
        onClick={controls.toggle}
        title={controls.isScrolling ? t('view.stopScrolling') : t('view.startScrolling')}
      >
        {controls.label}
      </button>
      <button
        className={`overlay-btn ${!controls.canSlowDown ? 'disabled' : ''}`}
        onClick={controls.canSlowDown ? handleDecreaseSpeed : undefined}
        title={controls.smart ? t('view.slower') : t('view.scrollSlower')}
      >
        <Icon icon={<ArrowDownIcon />} />
        {decreaseAnimKey > 0 && <span key={decreaseAnimKey} className="btn-ripple btn-ripple--slow" />}
      </button>
    </div>
  );
};

SuperScroll.propTypes = {
  song: PropTypes.object,
  continuousView: PropTypes.bool,
};

export default SuperScroll;
